import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SeoService } from '../../../services/seo/seo.service';
import { AnalyticsService } from '../../../services/analytics/analytics.service';
import { LandingEnquiryComponent } from './ui/landing-enquiry/landing-enquiry.component';
import { LandingCommercialSectionsComponent } from './ui/landing-commercial-sections/landing-commercial-sections.component';
import { LandingFaqComponent } from './ui/landing-faq/landing-faq.component';
import { LandingHeroComponent } from './ui/landing-hero/landing-hero.component';
import { LandingProgrammeExampleComponent } from './ui/landing-programme-example/landing-programme-example.component';
import { LandingStaticSectionsComponent } from './ui/landing-static-sections/landing-static-sections.component';
import { WhiteLabelCtaLocation, WhiteLabelFaqItem } from './typings';

@Component({
  selector: 'wil-landing-white-label',
  standalone: true,
  imports: [
    LandingCommercialSectionsComponent,
    LandingEnquiryComponent,
    LandingFaqComponent,
    LandingHeroComponent,
    LandingProgrammeExampleComponent,
    LandingStaticSectionsComponent,
  ],
  templateUrl: './landing-white-label.component.html',
  styleUrl: './landing-white-label.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class LandingWhiteLabelComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SeoService);
  private readonly analytics = inject(AnalyticsService);

  public readonly delegationCategories = [
    'Choosing What to Delegate',
    'Matching Tasks to People',
    'Setting Clear Outcomes',
    'Defining Decision Authority',
    'Providing Resources',
    'Checking Progress',
    'Giving Feedback',
    'Avoiding Micromanagement',
  ];
  public readonly conversationCategories = [
    'Preparing the Conversation',
    'Separating Facts From Assumptions',
    'Describing the Problem Clearly',
    'Listening Without Defensiveness',
    'Asking Useful Questions',
    'Managing Emotional Reactions',
    'Agreeing Next Actions',
    'Following Up',
  ];
  public readonly faqItems: readonly WhiteLabelFaqItem[] = [
    {
      id: 'own_categories',
      question: 'Can I use my own categories?',
      answer:
        'Yes. The wheel is configured with the agreed categories from your programme, together with category descriptions or scoring guidance. Self-service editing after launch is not included in the standard project.',
    },
    {
      id: 'scoring_scale',
      question: 'Can I choose the scoring scale?',
      answer:
        'Yes. The wheel can use a 5-, 8-, 10-point or another agreed scale. Guidance can explain the category as a whole or what different scores represent.',
    },
    {
      id: 'website_installation',
      question: 'Can it be installed on my website?',
      answer:
        'The assessment can be installed on many modern websites through a JavaScript web component. Compatibility is checked before the project is confirmed; a website URL is enough for the initial review.',
    },
    {
      id: 'participant_results',
      question: "Will I receive participants' results?",
      answer:
        'The standard installation allows each participant to download the result as an image. Sending results to the programme owner may be added when the website and email infrastructure support it. Automatic result storage is not included.',
    },
    {
      id: 'automatic_progress_tracking',
      question: 'Does it track progress automatically?',
      answer:
        'No automatic history or comparison is included. Participants can repeat the assessment and download separate result images for later comparison, reflection, or discussion.',
    },
    {
      id: 'setup_timeline',
      question: 'How long does setup take?',
      answer:
        'A standard installation is usually ready within three working days after final content and website access are available. Website restrictions or additional functionality may affect the timeline.',
    },
    {
      id: 'project_cost',
      question: 'What does it cost?',
      answer:
        'The first assessment wheel costs $650 and each additional wheel costs $50. This includes configuration, installation on one approved website, result-image download, checking, and two revision rounds. Additional integrations are quoted separately.',
    },
    {
      id: 'later_changes',
      question: 'Can I make changes later?',
      answer:
        'Two revision rounds are included before launch. Later content or configuration changes can be requested and may be quoted separately.',
    },
  ];
  public readonly expandedFaqItems = signal<ReadonlySet<number>>(new Set<number>());

  ngOnInit(): void {
    const routeData = this.route.snapshot.data;
    this.seoService.updateSeoTags({
      title: routeData['title'],
      description: routeData['description'],
    });
    this.analytics.track('embedded_assessment_landing_viewed', { source: 'embedded_assessment_landing' });
  }

  public toggleFaq(index: number): void {
    const faqItem = this.faqItems[index];
    if (faqItem && !this.expandedFaqItems().has(index)) {
      this.analytics.track('embedded_assessment_faq_opened', {
        source: 'embedded_assessment_landing',
        faq_question_id: faqItem.id,
      });
    }

    this.expandedFaqItems.update((expandedItems) => {
      const nextExpandedItems = new Set(expandedItems);
      if (nextExpandedItems.has(index)) {
        nextExpandedItems.delete(index);
      } else {
        nextExpandedItems.add(index);
      }
      return nextExpandedItems;
    });
  }

  public trackCta(ctaLocation: WhiteLabelCtaLocation): void {
    this.analytics.track('embedded_assessment_cta_clicked', {
      source: 'embedded_assessment_landing',
      cta_location: ctaLocation,
    });
  }
}
